import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { sumOrderItems } from '@/utilities/calculateItemMetrics';
import { QUERY_OMS_BIN_LOCATIONS } from './lookupQueries';
import { useOrderLookups } from './useOrderLookups';
import {
    binLocationOptions,
    binLocationOptionsWhere,
    decodeListParam,
    decodeScanParam,
    orderVisibilityWhere,
} from './utils';

const normalizeRange = (metric) => {
    if (!metric) return 0;
    return Array.isArray(metric) ? Math.max(...metric.map((weight) => parseFloat(weight))) : parseFloat(metric);
};

// These fields can hold a range rather than a plain number, so every read goes through
// normalizeRange, and each falls back to the per-unit field when the computed total is absent.
const itemWeight = (item) => normalizeRange(item.total_weight) || normalizeRange(item.weight);
const itemCubes = (item) => normalizeRange(item.total_cubes) || normalizeRange(item.cubes_per_unit);

export const useTotalWeight = (orders) => {
    return useMemo(() => sumOrderItems(orders, { value: itemWeight }), [orders]);
};

export const useTotalCubes = (orders) => {
    return useMemo(() => sumOrderItems(orders, { value: itemCubes }), [orders]);
};

/** Shown when an item label's lookup succeeds but finds no order. */
export const STALE_ITEM_LABEL_MESSAGE =
    'No order found for this item label. The item may have been edited since the label was printed.';

/**
 * The scanned label and picked bins from the URL, and the order ids their lookups and the search
 * term's lookups found. Reports a failed lookup, since its ids are then missing and a scan or bin
 * filter would otherwise show "0 orders" as if nothing matched. Also warns when a scanned item label
 * finds no order: editing an order's items replaces their ids, so a label printed before the edit
 * points at an item that no longer exists.
 *
 * @param {object} params
 * @param {URLSearchParams} params.searchParams The page's URL params.
 * @param {string[]} params.clientIds The viewer's client ids.
 * @param {(message: string) => void} params.notifyError Shows an error to the user.
 * @param {(message: string) => void} params.notifyWarning Shows a warning to the user.
 * @returns {{ activeScan: import('./utils').ScanTarget|null, pickedBins: string[],
 *   orderLookups: ReturnType<typeof useOrderLookups> }}
 */
export const useOrderLookupFilters = ({ searchParams, clientIds, notifyError, notifyWarning }) => {
    const searchTerm = searchParams.get('searchTerm');
    const scanParam = searchParams.get('scan');
    const binLocationsParam = searchParams.get('binLocations');
    const activeScan = useMemo(() => decodeScanParam(scanParam), [scanParam]);
    const pickedBins = useMemo(() => decodeListParam(binLocationsParam), [binLocationsParam]);
    const orderLookups = useOrderLookups({ searchTerm, scan: activeScan, binLocations: pickedBins, clientIds });

    // Read through refs so a new callback each render doesn't repeat the message.
    const notifyErrorRef = useRef(notifyError);
    notifyErrorRef.current = notifyError;
    const notifyWarningRef = useRef(notifyWarning);
    notifyWarningRef.current = notifyWarning;

    const { error, loading, scanOrderIds } = orderLookups;
    useEffect(() => {
        if (!error) return;
        captureException(error);
        notifyErrorRef.current("Couldn't look up orders by barcode, scan, or bin. Results may be incomplete.");
    }, [error]);

    const staleItemLabel =
        activeScan?.type === 'item' && !!clientIds?.length && !loading && !error && !scanOrderIds.length;
    useEffect(() => {
        if (staleItemLabel) notifyWarningRef.current(STALE_ITEM_LABEL_MESSAGE);
    }, [staleItemLabel, scanParam]);

    return { activeScan, pickedBins, orderLookups };
};

/**
 * Options for the Bin/Pool Location filter. The query joins every in-warehouse pallet to the
 * viewer's orders, so it waits until the filter is first opened; Apollo's cache makes reopening it
 * instant.
 *
 * @param {string[]} clientIds The viewer's client ids.
 * @param {boolean} filterOpen Whether the Bin/Pool Location filter is open.
 * @returns {Array<{ value: string, label: string, secondary?: string }>}
 */
export const useBinLocationFilterOptions = (clientIds, filterOpen) => {
    const { data } = useQuery(QUERY_OMS_BIN_LOCATIONS, {
        variables: { where: binLocationOptionsWhere(orderVisibilityWhere(clientIds)) },
        skip: !clientIds?.length || !filterOpen,
    });
    return useMemo(() => binLocationOptions(data?.pallet_items), [data]);
};

/**
 * The orders `where`, held at its last committed value while the order-id lookups load, so the
 * table doesn't briefly show the results of a half-built filter. Null on first load until the
 * lookups finish, so the orders query can skip instead of running a partial filter.
 *
 * @param {() => object} buildWhere Builds the current `where`.
 * @param {boolean} lookupsLoading Whether any order-id lookup is loading.
 * @returns {object|null}
 */
export const useHeldWhere = (buildWhere, lookupsLoading) => {
    const where = useMemo(() => (lookupsLoading ? null : buildWhere()), [buildWhere, lookupsLoading]);
    // Saved after commit, not during render, so a render React throws away can't leave behind a
    // filter the table never showed.
    const lastWhereRef = useRef(null);
    useEffect(() => {
        if (where) lastWhereRef.current = where;
    }, [where]);
    return where || lastWhereRef.current;
};
